import { useEffect, useState } from "react";
import { api, formatINR, formatDate } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/ui-helpers";
import { Search, X } from "lucide-react";
import { useClientPagination } from "@/hooks/useClientPagination";
import { PaginationControls } from "@/components/PaginationControls";

export default function GeneralLedger() {
  const [list, setList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [projectId, setProjectId] = useState("");
  const [search, setSearch] = useState("");

  async function load() {
    const qs = new URLSearchParams();
    if (fromDate) qs.set("from_date", fromDate);
    if (toDate) qs.set("to_date", toDate);
    if (projectId) qs.set("project_id", projectId);
    const { data } = await api.get(`/ledger/general?${qs}`);
    setList(data);
  }
  useEffect(() => { api.get("/projects-picker").then(r => setProjects(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fromDate, toDate, projectId]);

  const q = search.trim().toLowerCase();
  const filtered = q ? list.filter(e => [
    e.account_name, e.account_type, e.narration, e.project_name,
    e.ref_type, e.ref_number, String(e.debit || ""), String(e.credit || ""),
  ].some(v => (v || "").toString().toLowerCase().includes(q))) : list;

  const { paged, page, pageSize, total, setPage, setPageSize } = useClientPagination(filtered, { initialPageSize: 50 });

  return (
    <div>
      <PageHeader
        eyebrow="Accounting"
        title="General Ledger"
        subtitle="Chronological view of every double-entry posting across all accounts."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <select value={projectId} onChange={e => setProjectId(e.target.value)} className="border border-slate-300 px-3 py-2 text-xs">
              <option value="">All Projects</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.project_code} · {p.project_name}</option>)}
            </select>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="border border-slate-300 px-3 py-2 text-xs" />
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="border border-slate-300 px-3 py-2 text-xs" />
          </div>
        }
      />

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
        <div className="relative flex-1 sm:max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="gl-search"
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search account, narration, project, ref no, amount…"
            className="w-full pl-9 pr-8 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white"
          />
          {search && (
            <button
              type="button"
              data-testid="gl-search-clear"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              title="Clear">
              <X size={13} />
            </button>
          )}
        </div>
        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500" data-testid="gl-count">
          {filtered.length} of {list.length} entries{search ? ` · filtered by "${search}"` : ""}
        </div>
      </div>

      {filtered.length === 0 ? <EmptyState title="No entries" hint={search ? `No matches for "${search}"` : undefined} /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>{["Date", "Account", "Particulars", "Project", "Ref", "Debit", "Credit"].map(h =>
                  <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {paged.map(e => (
                  <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="py-2 px-4 text-xs text-slate-600 whitespace-nowrap">{formatDate(e.entry_date)}</td>
                    <td className="py-2 px-4 text-sm text-slate-800">
                      <span className="font-medium">{e.account_name}</span>
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">{e.account_type.replace(/_/g, " ")}</span>
                    </td>
                    <td className="py-2 px-4 text-sm text-slate-700">{e.narration}</td>
                    <td className="py-2 px-4 text-xs text-slate-600">{e.project_name || "—"}</td>
                    <td className="py-2 px-4 text-[10px] uppercase text-slate-500">{e.ref_type.replace(/_/g, " ")} {e.ref_number && `· ${e.ref_number}`}</td>
                    <td className="py-2 px-4 text-sm tabular">{e.debit ? formatINR(e.debit) : ""}</td>
                    <td className="py-2 px-4 text-sm tabular">{e.credit ? formatINR(e.credit) : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls page={page} pageSize={pageSize} total={total}
            onPage={setPage} onPageSize={setPageSize}
            pageSizeOptions={[25, 50, 100, 200]} />
        </Card>
      )}
    </div>
  );
}
